import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getJrSummary } from '../services/jrOverview.js';
import { usePolicy } from '../components/PolicyContext.jsx';

const SEMAFOR = {
  green: { label: 'zelená', cls: 'semafor semafor--grn' },
  orange: { label: 'oranžová', cls: 'semafor semafor--org' },
  red: { label: 'červená', cls: 'semafor semafor--red' },
};


const FILTERS = [
  { id: 'all', label: 'Všichni', test: () => true },
  { id: 'red', label: 'Červená', test: (m) => m.semafor === 'red' },
  { id: 'orange', label: 'Oranžová', test: (m) => m.semafor === 'orange' },
  { id: 'green', label: 'Zelená', test: (m) => m.semafor === 'green' },
  { id: 'missing', label: 'Bez zápisu', test: (m) => m.missing_yesterday },
];

const czk = (n) => `${Math.round(Number(n) || 0).toLocaleString('cs-CZ')} Kč`;
const dt = (v) => (v ? new Date(v).toLocaleDateString('cs-CZ') : '—');

export default function JrOverview() {
  const { policy } = usePolicy();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    getJrSummary()
      .then(setData)
      .catch((e) => setError(e.message || 'Přehled se nepodařilo načíst.'));
  }, []);

  const rows = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0];
    return (data?.maklers || []).filter(f.test);
  }, [data, filter]);

  if (error) return <p className="dashboard__error" role="alert">{error}</p>;
  if (!data) return <div className="dashboard__status">Načítá se…</div>;

  const t = data.totals;
  const levels = policy.levels.map((l) => l.name).filter((l) => t.by_level[l])
    .map((l) => `${t.by_level[l]} ${l.toLowerCase()}`)
    .join(' · ');
  const stamp = new Date(data.generated_at).toLocaleString('cs-CZ');

  return (
    <section className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">Přehled</h1>
          <p className="dashboard__lead">
            Stav k {stamp}. Provize: poslední import {dt(data.last_import)}.
          </p>
        </div>
        <Link className="button button--spot" to="/dashboard/import">Nahrát provize</Link>
      </div>

      <p className="card__hint">
        Stejná čísla vidí JCH. Report pro JCH chodí automaticky ve čtvrtek z uzavřeného
        týdne, e-mailem a na Disk – nic nepřipravuješ.
      </p>

      <div className="dashboard__cards">
        <article className="card">
          <span className="card__caption">Makléřů v Drillu</span>
          <span className="card__big">{t.count}</span>
          <span className="card__hint">{levels || '—'}</span>
        </article>
        <article className="card">
          <span className="card__caption">Semafor minulý týden</span>
          <span className="card__big">{t.semafor.green} zelená</span>
          <span className="card__hint">
            {t.semafor.orange} oranžová · {t.semafor.red} červená. Vůči vlastnímu plánu, jen
            z povinných oslovení.
          </span>
        </article>
        <article className="card">
          <span className="card__caption">Nábory tento měsíc</span>
          <span className="card__big">{t.acquisitions_month}</span>
          <span className="card__hint">{t.acquisitions_90d} za posledních 90 dní</span>
        </article>
        <article className="card">
          <span className="card__caption">Vyplacené provize</span>
          <span className="card__big">{czk(t.commission_month)}</span>
          <span className="card__hint">tento měsíc · nahráno do {dt(data.last_import)}</span>
        </article>
      </div>

      <div className="card">
        <h2 className="card__title">Úkoly a upozornění</h2>
        {data.alerts.length === 0 ? (
          <p className="card__text">Nic nečeká.</p>
        ) : (
          <ul className="jr-list">
            {data.alerts.map((a, i) => (
              <li key={i}>
                {a.makler_id ? (
                  <Link to={`/dashboard/database/${a.makler_id}`}>{a.text}</Link>
                ) : (
                  a.text
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="card__hint">
          Týden uzavíráš ty – v Rozjezdu po review (po–st), v přípravě bez review. Když ho do
          čtvrtka neuzavřeš, Drill ho uzavře sám těsně před odesláním reportu. Upozornění na
          makléře bez schůzek a náborů barvu semaforu nemění.
        </p>
      </div>

      <div className="jr-filters" role="tablist" aria-label="Filtr">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={filter === f.id ? 'button button--spot' : 'button button--secondary'}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>
      <p className="card__hint">Zobrazeno {rows.length} z {t.count} · tempo a provize v Kč</p>

      <div className="card jr-table-wrap">
        <table className="jr-table">
          <thead>
            <tr>
              <th>Makléř</th>
              <th>Fáze</th>
              <th>Semafor</th>
              <th>Tento týden</th>
              <th>Nábory 90 d</th>
              <th>Tempo · rozjednáno</th>
              <th>Za 12 měs.</th>
              <th>Nejbližší pevný bod</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.id}>
                <td>
                  <Link to={`/dashboard/database/${m.id}`}>{m.name}</Link>
                  {m.cohort && <div className="card__hint">{m.cohort}</div>}
                </td>
                <td style={{ color: m.level_color }}>{m.level}</td>
                <td>
                  {m.semafor ? (
                    <span className={SEMAFOR[m.semafor].cls}>{SEMAFOR[m.semafor].label}</span>
                  ) : (
                    <span className="semafor semafor--none">—</span>
                  )}
                </td>
                <td>{m.current_week ? `${m.current_week.done} / ${m.current_week.plan}` : '—'}</td>
                <td>{m.acquisitions_90d}</td>
                <td>
                  {czk(m.tempo_czk)}
                  <div className="card__hint">odhad rozj. {czk(m.rozjednano)} · {m.active_zakazky} zakázek</div>
                </td>
                <td>{czk(m.commission_12m)}</td>
                <td>
                  {m.next_event ? `${dt(m.next_event.date)} · ${m.next_event.title}` : '—'}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8}>V tomto filtru nikdo není.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2 className="card__title">Včera nezapsali</h2>
        {data.missing_yesterday.length === 0 ? (
          <p className="card__text">Všichni mají zapsáno.</p>
        ) : (
          <p className="card__text">{data.missing_yesterday.map((m) => m.name).join(', ')}</p>
        )}
        <p className="card__hint">
          N-8: e-mail JR i makléři po {data.entry_alert_rules?.missing_days ?? 3} pracovních dnech
          bez zápisu za sebou. N-9: e-mail JR od {data.entry_alert_rules?.from_month ?? 2}. měsíce
          při méně než {data.entry_alert_rules?.percent ?? 80} % dnů se zápisem v aktuálním
          kalendářním měsíci do včerejška. E-maily se odesílají od {data.entry_alert_rules?.hour ?? 8}:00
          (Europe/Prague), bez push notifikací. Svátky, tichá období, dovolená a omluvená
          nepřítomnost se nepočítají; uložený nulový den je zápis.
        </p>
      </div>
    </section>
  );
}
